<template>
  <div class="package-payment">
    <div class="page-header">
      <h1 class="page-title">{{ $t("subscriptions.purchasePackage") }}</h1>
      <!-- <the-breadcrumb :items="breadcrumbItems" /> -->
    </div>

    <div class="content-wrapper">
      <div v-if="loading" class="loading-container">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <div v-else-if="packageData" class="payment-container">
        <div class="payment-card">
          <div class="card-header">
            <h2>{{ $t("subscriptions.paymentSummary") }}</h2>
          </div>

          <div class="card-body">
            <!-- Package Summary -->
            <div class="package-summary">
              <div class="summary-image">
                <img :src="packageData.image" :alt="packageData.name" />
              </div>
              <div class="summary-details">
                <h3>{{ packageData.name }}</h3>
                <div class="summary-info">
                  <div class="info-row">
                    <span>{{ $t("subscriptions.duration") }}</span>
                    <span class="value"
                      >{{ packageData.duration }}
                      {{ $t("subscriptions.months") }}</span
                    >
                  </div>
                  <div class="info-row">
                    <span>{{ $t("subscriptions.basePrice") }}</span>
                    <span class="value"
                      >{{ packageData.paid_price }}
                      {{ $t("common.currency") }}</span
                    >
                  </div>
                  <div class="info-row">
                    <span
                      >{{ $t("subscriptions.tax") }} ({{
                        packageData.tax_value
                      }}%)</span
                    >
                    <span class="value"
                      >{{
                        (
                          packageData.total_with_tax - packageData.paid_price
                        ).toFixed(2)
                      }}
                      {{ $t("common.currency") }}</span
                    >
                  </div>
                  <div class="info-row total">
                    <span>{{ $t("subscriptions.totalPrice") }}</span>
                    <span class="value"
                      >{{ packageData.total_with_tax }}
                      {{ $t("common.currency") }}</span
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Method -->
            <div class="payment-method">
              <h3>{{ $t("subscriptions.paymentMethod") }}</h3>
              <div class="payment-options">
                <div
                  class="payment-option"
                  :class="{ selected: paymentMethod === 'online' }"
                  @click="paymentMethod = 'online'"
                >
                  <div class="option-icon">
                    <i class="fas fa-credit-card"></i>
                  </div>
                  <div class="option-content">
                    <span class="option-title">{{
                      $t("subscriptions.onlinePayment")
                    }}</span>
                    <span class="option-description">{{
                      $t("subscriptions.onlinePaymentDescription")
                    }}</span>
                  </div>
                  <div class="option-radio">
                    <i
                      v-if="paymentMethod === 'online'"
                      class="fas fa-check-circle"
                    ></i>
                    <i v-else class="far fa-circle"></i>
                  </div>
                </div>
              </div>
            </div>

            <!-- Terms and Conditions -->
            <div class="terms-section">
              <base-checkbox-input
                v-model="acceptTerms"
                :label="$t('subscriptions.acceptTerms')"
                color="primary"
              />
            </div>
          </div>

          <div class="card-footer">
            <base-button
              :btnText="$t('common.cancel')"
              styleType="cancel_btn"
              @fireClick="goBack"
            />
            <base-button
              :btnText="$t('subscriptions.completePayment')"
              styleType="primary_btn"
              :disabled="!acceptTerms || processing"
              :isLoading="processing"
              @fireClick="processPayment"
            />
          </div>
        </div>

        <!-- Payment Instructions -->
        <div class="payment-instructions">
          <div class="instruction-card">
            <i class="fas fa-info-circle"></i>
            <div class="instruction-content">
              <h4>{{ $t("subscriptions.paymentInstructions") }}</h4>
              <ul>
                <li>{{ $t("subscriptions.instruction1") }}</li>
                <li>{{ $t("subscriptions.instruction2") }}</li>
                <li>{{ $t("subscriptions.instruction3") }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <success-modal
      :show="showSuccessModal"
      :title="$t('subscriptions.paymentSuccess')"
      :message="successMessage"
      @close="handleSuccessClose"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import TheBreadcrumb from "@/components/structure/TheBreadcrumb.vue";
import SuccessModal from "@/components/modals/SuccessModal.vue";

export default {
  name: "PackagePayment",

  components: {
    TheBreadcrumb,
    SuccessModal,
  },

  data() {
    return {
      loading: false,
      processing: false,
      packageData: null,
      paymentMethod: "online",
      acceptTerms: false,
      showSuccessModal: false,
      successMessage: "",
    };
  },

  computed: {
    breadcrumbItems() {
      return [
        { text: this.$t("common.home"), to: "/" },
        {
          text: this.$t("subscriptions.title"),
          to: { name: "CurrentSubscription" },
        },
        {
          text: this.$t("subscriptions.availablePackages"),
          to: { name: "PackagesList" },
        },
        { text: this.$t("subscriptions.payment"), active: true },
      ];
    },
  },

  mounted() {
    this.loadPackageDetails();
  },

  methods: {
    ...mapActions("subscriptions", ["fetchPackageDetails", "purchasePackage"]),

    async loadPackageDetails() {
      const packageId = this.$route.params.id;
      this.loading = true;

      try {
        const response = await this.fetchPackageDetails(packageId);
        this.packageData = response.data.data;
      } catch (error) {
        const apiMessage =
          error.response?.data?.message ||
          (error.response?.data?.errors &&
            (error.response.data.errors.message ||
              JSON.stringify(error.response.data.errors))) ||
          error.message ||
          this.$t("subscriptions.errors.loadFailed");
        this.$message.error(apiMessage);
        this.goBack();
      } finally {
        this.loading = false;
      }
    },

    async processPayment() {
      if (!this.acceptTerms) {
        this.$message.error(this.$t("subscriptions.acceptTermsRequired"));
        return;
      }

      this.processing = true;

      try {
        const response = await this.purchasePackage({
          packageId: this.$route.params.id,
          paymentMethod: this.paymentMethod,
        });

        // In a real scenario, you would redirect to payment gateway
        console.log("Payment Response:", response);
        // For now, showing success message
        if (response && response.invoiceURL) {
          // Redirect to payment gateway
          window.location.href = response.invoiceURL;
        } else {
          // Show success modal
          this.successMessage = this.$t(
            "subscriptions.subscriptionSuccessMessage",
            {
              packageName: this.packageData.name,
            }
          );
          this.showSuccessModal = true;
        }
      } catch (error) {
        const apiMessage =
          error.response?.data?.message ||
          (error.response?.data?.errors &&
            (error.response.data.errors.message ||
              JSON.stringify(error.response.data.errors))) ||
          error.message ||
          this.$t("subscriptions.errors.paymentFailed");
        this.$message.error(apiMessage);
      } finally {
        this.processing = false;
      }
    },

    handleSuccessClose() {
      this.showSuccessModal = false;
      this.$router.push({ name: "CurrentSubscription" });
    },

    goBack() {
      this.$router.back();
    },
  },
};
</script>

<style lang="scss" scoped>
.package-payment {
  padding: 20px;

  .page-header {
    margin-bottom: 30px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 10px;
      color: var(--theme_text_clr);
    }
  }

  .content-wrapper {
    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 400px;
    }

    .payment-container {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 24px;

      @media (max-width: 968px) {
        grid-template-columns: 1fr;
      }

      .payment-card {
        background: var(--theme_second_bg_clr);
        border-radius: 12px;
        box-shadow: 0 2px 8px var(--simple_shadow_clr);
        overflow: hidden;

        .card-header {
          padding: 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: #fff;

          h2 {
            margin: 0;
            font-size: 20px;
            font-weight: 600;
          }
        }

        .card-body {
          padding: 30px;

          .package-summary {
            display: flex;
            gap: 20px;
            padding: 20px;
            background: var(--theme_main_bg_clr);
            border-radius: 8px;
            margin-bottom: 30px;

            @media (max-width: 768px) {
              flex-direction: column;
            }

            .summary-image {
              img {
                width: 120px;
                height: 120px;
                object-fit: cover;
                border-radius: 8px;
              }
            }

            .summary-details {
              flex: 1;

              h3 {
                font-size: 18px;
                font-weight: 600;
                color: var(--theme_text_clr);
                margin: 0 0 15px 0;
              }

              .summary-info {
                display: flex;
                flex-direction: column;
                gap: 10px;

                .info-row {
                  display: flex;
                  justify-content: space-between;
                  font-size: 14px;
                  color: #666;

                  .value {
                    font-weight: 600;
                    color: #333;
                  }

                  &.total {
                    padding-top: 10px;
                    border-top: 2px solid #ddd;
                    font-size: 16px;
                    font-weight: 600;
                    color: #333;

                    .value {
                      color: #667eea;
                      font-size: 20px;
                    }
                  }
                }
              }
            }
          }

          .payment-method {
            margin-bottom: 30px;

            h3 {
              font-size: 18px;
              font-weight: 600;
              color: #333;
              margin-bottom: 15px;
            }

            .payment-options {
              .payment-option {
                display: flex;
                align-items: center;
                gap: 15px;
                padding: 20px;
                border: 2px solid var(--border_clr);
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.3s ease;

                &:hover {
                  border-color: var(--main_theme_clr);
                  background: var(--theme_main_bg_clr);
                }

                &.selected {
                  border-color: var(--main_theme_clr);
                  background: var(--theme_main_bg_clr);
                }

                .option-icon {
                  i {
                    font-size: 32px;
                    color: var(--main_theme_clr);
                  }
                }

                .option-content {
                  flex: 1;
                  display: flex;
                  flex-direction: column;
                  gap: 5px;

                  .option-title {
                    font-size: 16px;
                    font-weight: 600;
                    color: var(--theme_text_clr);
                  }

                  .option-description {
                    font-size: 14px;
                    color: var(--theme_secondary_text_clr);
                  }
                }

                .option-radio {
                  i {
                    font-size: 24px;
                    color: var(--main_theme_clr);
                  }
                }
              }
            }
          }

          .terms-section {
            padding: 20px;
            background: var(--theme_main_bg_clr);
            border-radius: 8px;
            border: 1px solid var(--border_clr);
          }
        }

        .card-footer {
          padding: 20px 30px;
          border-top: 1px solid var(--border_clr);
          display: flex;
          justify-content: space-between;
          gap: 15px;

          @media (max-width: 768px) {
            flex-direction: column;
          }
        }
      }

      .payment-instructions {
        .instruction-card {
          background: var(--theme_second_bg_clr);
          border-radius: 12px;
          box-shadow: 0 2px 8px var(--simple_shadow_clr);
          padding: 24px;
          display: flex;
          gap: 15px;

          > i {
            font-size: 24px;
            color: var(--main_theme_clr);
            margin-top: 5px;
          }

          .instruction-content {
            flex: 1;

            h4 {
              font-size: 16px;
              font-weight: 600;
              color: var(--theme_text_clr);
              margin: 0 0 15px 0;
            }

            ul {
              margin: 0;
              padding: 0 0 0 20px;
              list-style: disc;

              li {
                font-size: 14px;
                color: var(--theme_secondary_text_clr);
                line-height: 1.6;
                margin-bottom: 8px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
